import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Calendar, Clock } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { BLOG } from "@/data/content";

export function Blog() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [feature, ...rest] = BLOG;

  return (
    <main data-testid="blog-page" className="bg-brand-cream pt-28 pb-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand-brown mb-4">The Field Journal</p>
          <h1 className="font-heading font-extrabold text-brand-ink tracking-tighter text-4xl sm:text-6xl max-w-3xl leading-[0.98]">
            Modern farming tips & machinery insights.
          </h1>
        </Reveal>

        <Reveal delay={0.1}>
          <Link to={`/blog/${feature.slug}`} data-testid={`blog-feature-${feature.slug}`} className="group mt-14 grid lg:grid-cols-2 gap-8 rounded-2xl border border-brand-ink/10 bg-white overflow-hidden hover:shadow-xl transition-shadow">
            <div className="overflow-hidden">
              <img src={feature.image} alt={feature.title} className="h-64 lg:h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-8 lg:p-10 flex flex-col justify-center">
              <span className="inline-flex w-fit items-center rounded-full bg-brand-green/10 text-brand-green px-3 py-1 font-mono text-xs uppercase tracking-wider">{feature.tag}</span>
              <h2 className="mt-4 font-heading font-bold text-2xl sm:text-3xl text-brand-ink tracking-tight leading-tight">{feature.title}</h2>
              <p className="mt-3 text-brand-ink/70 leading-relaxed">{feature.excerpt}</p>
              <div className="mt-6 flex items-center gap-5 text-xs text-brand-ink/50 font-mono">
                <span className="flex items-center gap-1.5"><Calendar className="size-3.5" />{feature.date}</span>
                <span className="flex items-center gap-1.5"><Clock className="size-3.5" />{feature.readTime}</span>
              </div>
            </div>
          </Link>
        </Reveal>

        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((b, i) => (
            <Reveal key={b.slug} delay={i * 0.08}>
              <Link to={`/blog/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="group flex flex-col h-full rounded-2xl border border-brand-ink/10 bg-white overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow]">
                <div className="overflow-hidden">
                  <img src={b.image} alt={b.title} className="h-48 w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <span className="inline-flex w-fit items-center rounded-full bg-brand-brown/10 text-brand-brown px-3 py-1 font-mono text-[11px] uppercase tracking-wider">{b.tag}</span>
                  <h3 className="mt-3 font-heading font-bold text-lg text-brand-ink tracking-tight leading-tight">{b.title}</h3>
                  <p className="mt-2 text-sm text-brand-ink/65 leading-relaxed flex-1">{b.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-brand-green">Read article <ArrowUpRight className="size-4" /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </main>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  const post = BLOG.find((b) => b.slug === slug);

  if (!post) {
    return (
      <main className="min-h-screen grid place-items-center bg-brand-cream" data-testid="blogpost-not-found">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-bold text-brand-ink">Article not found</h1>
          <Link to="/blog" className="mt-6 inline-block text-brand-green font-medium">Back to blog</Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="blog-post" className="bg-brand-cream pt-28 pb-24">
      <article className="mx-auto max-w-3xl px-5 lg:px-8">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-brand-ink/60 hover:text-brand-green transition-colors mb-8">
          <ArrowLeft className="size-4" /> The Field Journal
        </Link>
        <span className="inline-flex items-center rounded-full bg-brand-green/10 text-brand-green px-3 py-1 font-mono text-xs uppercase tracking-wider">{post.tag}</span>
        <h1 className="mt-5 font-heading font-extrabold text-brand-ink tracking-tighter text-3xl sm:text-5xl leading-[1.02]">{post.title}</h1>
        <div className="mt-5 flex items-center gap-5 text-xs text-brand-ink/50 font-mono">
          <span className="flex items-center gap-1.5"><Calendar className="size-3.5" />{post.date}</span>
          <span className="flex items-center gap-1.5"><Clock className="size-3.5" />{post.readTime}</span>
        </div>
        <img src={post.image} alt={post.title} className="mt-8 w-full h-72 object-cover rounded-2xl" />
        <div className="mt-8 space-y-5">
          {post.body.map((para, i) => (
            <p key={i} className="text-brand-ink/80 leading-relaxed text-lg">{para}</p>
          ))}
        </div>
      </article>
    </main>
  );
}
